import React, { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  clockModeConfigAtom,
  clockModeStateAtom,
  // clockModeConfigModeAtom,
  clockModeTimeAtom,
  clockModeTimeUpdaterAtom,
  updateClockModeStateAtom,
} from "../../atoms/v3/clockModeAtoms";
import { updateTaskAtom } from "../../atoms/v0/taskAtoms";
import { globalTaskEditAtom } from "../../atoms/v3/taskAtoms";
import { syncUserTrackingStateAtom } from "../../atoms/v3/focusAtoms";
import { ClockModeLayout } from "./components/clock/ClockModeLayout";
import { UndoToastContainer } from "../../components/v3/UndoToastContainer";
import { TaskDetailSheet } from "./components/tasks/TaskDetailSheet";
// import ClockModeSettings from './components/clock/ClockModeSettings';

// 导入时钟模式专用样式
import "../../styles/clockMode.css";
import ClockModeBackground from "./components/clock/components/ClockModeBackground";

/**
 * 时钟模式主页面
 * 提供沉浸式的时钟显示和可配置的信息区域
 */
export const ClockMode: React.FC = () => {
  const navigate = useNavigate();

  // 状态管理
  const config = useAtomValue(clockModeConfigAtom);
  const state = useAtomValue(clockModeStateAtom);
  const [, updateState] = useAtom(updateClockModeStateAtom);
  // const [isConfigMode, setIsConfigMode] = useAtom(clockModeConfigModeAtom);
  const currentTime = useAtomValue(clockModeTimeAtom);
  const [, startTimeUpdater] = useAtom(clockModeTimeUpdaterAtom);

  // 任务相关状态管理
  const updateTask = useSetAtom(updateTaskAtom);
  const [globalTaskEdit, setGlobalTaskEdit] = useAtom(globalTaskEditAtom);
  const [, syncUserTrackingState] = useAtom(syncUserTrackingStateAtom);

  // 本地状态
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  // 引用
  const pageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeUpdaterCleanupRef = useRef<(() => void) | null>(null);

  // 计算有效主题
  const effectiveTheme = state.theme;



  // 处理屏幕方向
  useEffect(() => {
    if (state.orientation === "auto") return;

    const handleOrientationChange = async () => {
      if ("screen" in window && "orientation" in window.screen) {
        try {
          if (state.orientation === "landscape") {
            await (window.screen.orientation as any).lock("landscape");
          } else if (state.orientation === "portrait") {
            await (window.screen.orientation as any).lock("portrait");
          }
        } catch (error) {
          console.warn("Screen orientation lock failed:", error);
        }
      }
    };

    handleOrientationChange();

    return () => {
      if ("screen" in window && "orientation" in window.screen) {
        try {
          (window.screen.orientation as any).unlock();
        } catch (error) {
          console.warn("Screen orientation unlock failed:", error);
        }
      }
    };
  }, [state.orientation]);

  // 处理屏幕常亮
  useEffect(() => {
    let wakeLock: any = null;

    const requestWakeLock = async () => {
      if (state.keepScreenOn && "wakeLock" in navigator) {
        try {
          wakeLock = await (navigator as any).wakeLock.request("screen");
          console.log("Wake lock acquired");
        } catch (error) {
          console.warn("Wake lock request failed:", error);
        }
      }
    };

    requestWakeLock();

    return () => {
      if (wakeLock) {
        wakeLock.release();
        console.log("Wake lock released");
      }
    };
  }, [state.keepScreenOn]);

  // 应用主题覆盖
  useEffect(() => {
    if (!pageRef.current) return;

    const container = pageRef.current;
    container.classList.remove("clock-mode-light", "clock-mode-dark");
    container.classList.add(`clock-mode-${effectiveTheme}`);
  }, [effectiveTheme]);

  // 应用强制旋转
  // useEffect(() => {
  //   if (!containerRef.current) return;

  //   const container = containerRef.current;

  //   // 移除所有旋转类
  //   container.classList.remove('clock-mode-rotate-90', 'clock-mode-rotate-180', 'clock-mode-rotate-270');

  //   // 应用当前旋转
  //   if (state.forceRotation !== 'none') {
  //     container.classList.add(`clock-mode-${state.forceRotation}`);
  //   }
  // }, [state.forceRotation]);

  // 处理退出
  const handleExit = () => {
    // 清理临时屏幕设置，但保留用户的屏幕常亮偏好
    updateState({
      isFullscreen: false,
      orientation: "auto",
    });

    navigate("/v3/me");
  };

  // 处理长按进入配置模式
  const handleLongPress = () => {
    // 暂时不实现配置模式
    console.log("Long press detected");
  };

  // 处理撤销操作
  const handleUndo = useCallback(async (taskId: string, previousState: boolean) => {
    try {
      await updateTask({
        id: taskId,
        done: previousState,
      });
    } catch (error) {
      console.error('时钟模式 Undo 操作失败:', error);
    }
  }, [updateTask]);

  // 处理任务详情关闭
  const handleCloseTaskDetail = useCallback(() => {
    setIsTaskDetailOpen(false);
    setSelectedTaskId(null);
    // 同时清理全局任务编辑状态
    setGlobalTaskEdit({
      taskId: null,
      isOpen: false,
      triggerSource: 'normal'
    });
  }, [setGlobalTaskEdit]);

  // 监听全局任务编辑状态
  useEffect(() => {
    if (globalTaskEdit.isOpen && globalTaskEdit.taskId) {
      setSelectedTaskId(globalTaskEdit.taskId);
      setIsTaskDetailOpen(true);
    }
  }, [globalTaskEdit]);

  // 启动时间更新器和云端状态同步
  useEffect(() => {
    timeUpdaterCleanupRef.current = startTimeUpdater();

    // 同步云端追踪状态
    syncUserTrackingState();

    return () => {
      if (timeUpdaterCleanupRef.current) {
        timeUpdaterCleanupRef.current();
      }
    };
  }, [startTimeUpdater, syncUserTrackingState]);

  return (
    <div ref={pageRef} className={`clock-mode-page`}>
      <ClockModeBackground />
      
      <div
        ref={containerRef}
        className={`
        clock-mode-container
      `}
      >
        {/* 主布局 */}
        <ClockModeLayout
          config={config}
          currentTime={currentTime}
          onLongPress={handleLongPress}
          onExit={handleExit}
          state={state}
          onStateUpdate={updateState}
          // onEnterConfigMode={() => setIsConfigMode(true)}
        />

        {/* 配置弹窗
      <ClockModeSettings
        isOpen={isConfigMode}
        onClose={() => setIsConfigMode(false)}
        config={config}
        state={state}
      /> */}
      </div>

      {/* 撤销弹窗容器 */}
      <UndoToastContainer onUndo={handleUndo} />

      {/* 任务编辑模态框 */}
      <TaskDetailSheet
        taskId={selectedTaskId}
        isOpen={isTaskDetailOpen}
        onClose={handleCloseTaskDetail}
      />
    </div>
  );
};

export default ClockMode;
